import * as d3 from 'd3';
import * as _ from 'lodash';

import { ChartBase } from './ChartBase';
import { SvgManager } from '../SvgManager';
import { IAxis } from '../pieces/axes/index';
import { ISeries, LineSeries } from '../pieces/series/index';
import { IChartOptions } from '../../models/IChartOptions';

export class ChartWithAxesBase extends ChartBase {
    private _seriesList: any[];
    private _horizontalGridLines: d3.Selection<any>;
    private _verticalGridLines: d3.Selection<any>;
    private _displayOptions: any;
    private _tickCount = 6;

    constructor(chartOptions: IChartOptions, private svgManager: SvgManager, classed: string) {
        super(chartOptions, svgManager, classed, true);
        this.init();
        this._seriesList = [];
        this.translate();
    }

    private init() {
        this.xAxis().tickCount(this._tickCount);
        this.yAxis().tickCount(this._tickCount);

        this._horizontalGridLines = this.plotAreaGroup()
            .insert('g', '.series-group')
            .classed('horizontal-gridlines', true)
            .classed('gridlines', true);

        this._verticalGridLines = this.plotAreaGroup()
            .insert('g', '.series-group')
            .classed('vertical-gridlines', true)
            .classed('gridlines', true);
    }

    private updateHorizontalGridlines() {
        this.updateGridlines(this.isHorizontalGridlinesVisible(), this._horizontalGridLines,
            'left', this.plotAreaWidth(), this.yAxis());
    }

    private updateVerticalGridlines() {
        this.updateGridlines(this.isVerticalGridlinesVisible(), this._verticalGridLines, 'top', this.plotAreaHeight(), this.xAxis());
    }

    private updateGridlines(visible: boolean, group: d3.Selection<any>, orient: string, size: number,
        axis: IAxis) {
        if (visible) {
            group.style('visibility', 'visible');
            const gridLines = d3.svg.axis()
                .scale(axis.scale())
                .orient(orient)
                .ticks(this._tickCount)
                .tickSize(-size, 0)
                .tickFormat(() => '');
            gridLines(group);
            group
                .selectAll('line')
                .classed('gridline', true);
            //.attr('transform', 'translate(' + this.translateX() + ',' + this.translateY() + ')');
        } else {
            group.style('visibility', 'collapse');
        }
    }

    private isHorizontalGridlinesVisible() {
        return this._displayOptions ? this._displayOptions.isHorizontalGridlinesVisible : false;
    }

    private isVerticalGridlinesVisible() {
        return this._displayOptions ? this._displayOptions.isVerticalGridlinesVisible : false;
    }

    private xAxisFormat() {
        let format: string;
        if (this._displayOptions && this._displayOptions.xAxisFormat) {
            format = this._displayOptions.xAxisFormat;
        } else {
            format = 'f';
        }
        return format;
    }

    private yAxisFormat() {
        let format: string;
        if (this._displayOptions && this._displayOptions.yAxisFormat) {
            format = this._displayOptions.yAxisFormat;
        } else {
            format = 'f';
        }
        return format;
    }

    xAxisTitle(value?: string): string {
        const title = this.xAxis().titleText(value);
        return title;
    }

    isXAxisVisible(value?: boolean): boolean {
        const isVisible = this.xAxis().isTitleVisible(value);
        this.resize()(this.width(), this.height());
        this.updateHorizontalGridlines();
        this.updateVerticalGridlines();
        return isVisible;
    }

    yAxisTitle(value?: string): string {
        const title = this.yAxis().titleText(value);
        return title;
    }

    isYAxisVisible(value?: boolean): boolean {
        const isVisible = this.yAxis().isTitleVisible(value);
        this.resize()(this.width(), this.height());
        this.updateHorizontalGridlines();
        this.updateVerticalGridlines();
        return isVisible;
    }

    resizeAxes() {
        if (this.xAxis()) {
            this.xAxis().resize(this.plotAreaWidth());
            this.updateVerticalGridlines();
        }
        if (this.yAxis()) {
            this.yAxis().resize(this.plotAreaHeight());
            this.updateHorizontalGridlines();
        }
    }

    translate() {
        this.moveAxes();
    }

    updateSeries(newSeriesList: any[]) {
        try {
            const toRemove = _.filter(this._seriesList, s => !_.find(newSeriesList, ns => ns.identifier === s.identifier()));
            toRemove.forEach(s => {
                this.removeSeries(s);
            });
            const self = this;
            newSeriesList.forEach(ns => {
                let existing: any = _.find(self._seriesList, s => s.identifier() === ns.identifier);
                if (ns.isXAxis) {
                    ns.isVisible = false;
                }
                if (!existing) {
                    existing = self.addSeries(ns.name, ns.color, ns.value, ns.identifier, ns.selector, null, ns.isXAxis);
                }
                if (ns.isXAxis) {
                    existing.isVisible(false);
                }
                existing.isSymbolVisible(ns.isSymbolVisible);
                existing.color(ns.color);
                existing.symbol(d3.svg.symbol().type(ns.symbol));
                existing.isLineVisible(ns.isLineVisible);
                existing.legendText(ns.name);
                existing.interpolate(ns.renderingMode);
                if (ns.coloringConditions) {
                    existing.coloringConditions(ns.coloringConditions);
                }
                if (existing.legendAttributes) {
                    existing.legendAttributes(ns.attributes);
                }
                existing.legendText(ns.name);
                existing.click = ns.click;

                if (existing.crosshairFormat) {
                    existing.crosshairFormat(ns.crosshairFormat);
                }
            });
            this.legend().series(self._seriesList);
        } catch (e) {
            console.error(e);
        }
    }

    addSeries(seriesName: string,
        color: string,
        value: { (data: any): number },
        identifier,
        selector,
        symbol?,
        isXAxis?: boolean): ISeries {
        const series = new LineSeries(this.seriesGroup(), seriesName, color, value, identifier, selector);
        if (symbol) {
            series.symbol(d3.svg.symbol().type(symbol));
            series.isSymbolVisible(true);
        }
        series.isSymbolVisible(true);
        series.isXAxis(isXAxis);
        //this.legend().addSeries(series);
        series.isLineVisible(false);
        this._seriesList.push(series);
        this.legend().series(this._seriesList);
        return series;
    }

    removeSeries(series) {
        this.seriesGroup().select('#seriesId' + series.identifier()).remove();
        _.remove(this._seriesList, s => s.identifier() === series.identifier());
        this.legend().series(this._seriesList);
        if (series) {
            //this.legend().removeSeries(series);
        }
    }

    updateXAxis(min, max) {
        this.xAxis().tickFormat((d) => d3.format(this.xAxisFormat())(d));
        this.xAxis().update(min, max);
        this.updateVerticalGridlines();
    }

    updateYAxis(min, max) {
        this.yAxis().tickFormat((d) => d3.format(this.yAxisFormat())(d));
        this.yAxis().update(min, max);
        this.updateHorizontalGridlines();
    }

    series() {
        return this._seriesList;
    }

    displayOptions(value?: any): any {
        if (value) {
            this._displayOptions = value;
            this.updateHorizontalGridlines();
            this.updateVerticalGridlines();
        }
        return this._displayOptions;
    }
}